/**
 * Talking to the API.
 *
 * Same origin, so `credentials: 'same-origin'` is enough and there is no CORS
 * preflight on anything. The session travels as an HttpOnly cookie the browser
 * attaches itself — nothing here reads or stores a token, which is the point of
 * it being HttpOnly.
 */

import type { CurriculumNode, ObjectiveStatus, ReachWindow } from '@nabu/shared';

export interface ApiError {
  error: string;
  status: number;
  /**
   * Which of several things was wrong, when the error code alone does not say.
   *
   * The reach window is the case that needs it: one `invalid_window` covers a cap
   * out of range, an unreadable time, an empty window and one that wraps past
   * midnight, and a form of three controls cannot be corrected from the word
   * "invalid". Absent on every refusal that is already specific.
   */
  reason?: string;
}

async function post<T>(path: string, body: unknown): Promise<T | ApiError> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  // Parsed before the status is checked, because the API returns its reasons in
  // the body — a bare status would lose "rate_limited" versus "invalid_number",
  // which are different things to tell somebody.
  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'unreadable_response', status: response.status };
  }

  if (!response.ok) {
    const failure = parsed as { error?: string; reason?: string };
    return {
      error: failure.error ?? 'request_failed',
      status: response.status,
      ...(failure.reason ? { reason: failure.reason } : {}),
    };
  }
  return parsed as T;
}

export function isError(value: unknown): value is ApiError {
  return typeof value === 'object' && value !== null && 'error' in value && 'status' in value;
}

export interface Session {
  signedIn: boolean;
  userId?: string;
  locale?: string;
}

export async function whoAmI(): Promise<Session> {
  try {
    const response = await fetch('/api/auth/me', { credentials: 'same-origin' });
    if (!response.ok) return { signedIn: false };
    return (await response.json()) as Session;
  } catch {
    // Offline, or the API is down. Not signed in is the safe answer and the app
    // still renders — a landing page must not blank out because a fetch failed.
    return { signedIn: false };
  }
}

export const startSignIn = (phone: string) => post<{ ok: true }>('/api/auth/start', { phone });

export const verifyCode = (phone: string, code: string) =>
  post<{ ok: true; isNew: boolean; learner: { name: string; locale: string } }>(
    '/api/auth/verify',
    {
      phone,
      code,
      locale: document.documentElement.lang || 'es',
      // Sent from the browser because only the browser knows it, and quiet hours
      // are meaningless without it. Captured at sign-in so the very first
      // schedule is already in the learner's own time.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  );

export const signOut = () => post<{ ok: true }>('/api/auth/signout', {});

// ---- Objectives ----

export interface Objective {
  objectiveId: string;
  title: Record<string, string>;
  description: string;
  target: { mode: 'score'; targetScore: number } | { mode: 'date'; targetDate: string };
  // The union comes from the shared domain rather than being restated here, so
  // adding a status server-side breaks the `obj.status.*` copy at compile time
  // instead of rendering a raw identifier like `researching` at a learner.
  status: ObjectiveStatus;
  createdAt: string;
  /** Absent on objectives closed before the field existed, and on live ones. */
  closedAt?: string;
  previousStatus?: ObjectiveStatus;
  /**
   * Why the last research pass did not finish, when it did not.
   *
   * A failed pass puts the objective back to `drafting`, which on its own is
   * indistinguishable from never having started one — the screen would offer the
   * button again as though nothing had happened, and a learner who pressed it
   * twice would be told twice that it takes fifteen minutes without ever being
   * told what went wrong.
   *
   * This carries *only* failures. A successful pass writes its summary to
   * `researchDetail` and clears this, so the screen never has to work out which
   * of the two it is holding.
   */
  researchError?: string;
  /** What a finished research pass produced. Never a failure. */
  researchDetail?: string;
  /**
   * Why the last curriculum build did not finish, when it did not.
   *
   * A separate field from the success summary, and that is load-bearing here in a
   * way it is not for research: a failed synthesis returns the objective to
   * `review`, which is exactly where a *successful* one leaves it too. There is no
   * status that tells the two apart, so the field has to.
   */
  synthesisError?: string;
  /** What a finished synthesis built — "44 nodes from 1 documents". Never a failure. */
  synthesisDetail?: string;
  /**
   * Present once a complete set of questions exists, naming which set.
   *
   * This is the only signal that generation finished, because generation
   * deliberately never moves the status — see `startItemGeneration`. A run that
   * failed leaves no version behind, which is what lets the next run clean up after
   * it.
   */
  itemVersion?: string;
  /** What generation produced — "180 items across 45 nodes". Never a failure. */
  itemsDetail?: string;
  /** Why generation did not finish. Never a count. */
  itemsError?: string;
}

/**
 * The statuses an objective can be brought back from.
 *
 * `shelved` sits alongside `done` and `abandoned` because all three are equally
 * finished as far as the screen is concerned — but it is emphatically not a
 * synonym for `abandoned`, and the copy for the three is written separately. See
 * the note on `Objective.status` in the shared domain.
 */
const CLOSED_STATUSES: readonly ObjectiveStatus[] = ['done', 'abandoned', 'shelved'];

export const isClosed = (objective: Objective) => CLOSED_STATUSES.includes(objective.status);

export async function listObjectives(): Promise<{ objectives: Objective[] } | ApiError> {
  try {
    const response = await fetch('/api/objectives', { credentials: 'same-origin' });
    const parsed = (await response.json()) as unknown;
    if (!response.ok) {
      return { error: (parsed as { error?: string }).error ?? 'request_failed', status: response.status };
    }
    return parsed as { objectives: Objective[] };
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

export const createObjective = (
  description: string,
  target: Objective['target'],
) => post<{ objective: Objective }>('/api/objectives', { description, target });

/** The objective being worked on, or null. There is at most one. */
export async function currentObjective(): Promise<{ objective: Objective | null } | ApiError> {
  try {
    const response = await fetch('/api/objectives/current', { credentials: 'same-origin' });
    const parsed = (await response.json()) as unknown;
    if (!response.ok) {
      return { error: (parsed as { error?: string }).error ?? 'request_failed', status: response.status };
    }
    return parsed as { objective: Objective | null };
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

/** Changes the target alone. The description is settled once and never resent. */
export const updateTarget = (objectiveId: string, target: Objective['target']) =>
  post<{ target: Objective['target'] }>('/api/objectives/target', { objectiveId, target });

export const closeObjective = (objectiveId: string, status: 'done' | 'abandoned') =>
  post<{ ok: true }>('/api/objectives/close', { objectiveId, status });

/**
 * Brings a closed objective back, whether or not one is already live.
 *
 * One call for both cases deliberately: the browser does not decide whether
 * something has to be set aside first. `status` is where the objective landed —
 * it resumes what it was doing rather than restarting as a draft — and `shelved`
 * names the objective that was moved out of the way, absent when none was.
 */
export const reactivateObjective = (objectiveId: string) =>
  post<{ ok: true; objectiveId: string; status: ObjectiveStatus; shelved?: string }>(
    '/api/objectives/reactivate',
    { objectiveId },
  );

/**
 * Asks for the research pass. Answers 202, not 200.
 *
 * "Accepted and running", because the pass takes minutes and an HTTP request that
 * waited for it would be cut off at the edge long before it finished. The
 * returned status is what the objective is *now*; where it got to afterwards is
 * only ever read back from the objective itself.
 *
 * Valid from `drafting` alone, and the API refuses a second start with
 * `not_drafting` rather than starting one — the status change is the lock. That
 * refusal is the backstop, not the plan: research is the most expensive call the
 * system makes, so the screen must not offer a second start in the first place.
 */
export const startResearch = (objectiveId: string) =>
  post<{ ok: true; status: 'researching' }>('/api/objectives/research', { objectiveId });

/**
 * Asks for the curriculum to be built from whatever has been read so far.
 *
 * Valid from `review`. `no_material` is a different failure from `not_ready` and
 * is kept apart deliberately: one means the objective has not got there yet, the
 * other means it has and there is nothing in the content store to build from —
 * which is a thing the learner can act on by adding a page of their own.
 */
export const startSynthesis = (objectiveId: string) =>
  post<{ ok: true; status: 'synthesising' }>('/api/objectives/synthesise', { objectiveId });

/**
 * Starts writing the questions for the curriculum.
 *
 * Unlike research and synthesis this leaves the status alone, so there is no
 * `status` in the answer to read progress from. A Message Batch is minutes at best
 * and up to a day by contract, and an in-progress status held that long would lock
 * the objective out of everything else — so `itemVersion` appearing on the
 * objective is what says it finished.
 */
export const startItemGeneration = (objectiveId: string) =>
  post<{ ok: true }>('/api/objectives/items', { objectiveId });

/**
 * "Start" — the word that turns a plan into something that will interrupt you.
 *
 * Deliberately its own act rather than the end of building the curriculum. It is
 * also where the learner's memory begins to exist: every item they will be asked
 * gets a review state here, which is what the due sweep looks for.
 */
export const activateObjective = (objectiveId: string) =>
  post<{ ok: true; items: number; nodes: number }>('/api/objectives/activate', { objectiveId });

export interface ResearchDocument {
  key: string;
  title: string;
  language: string;
  producedAt: string;
  origin: string;
  body: string;
  truncated: boolean;
  characters: number;
}

/**
 * What research found, so it can be read before a curriculum is built from it.
 *
 * Not part of `listSources`: a research pass creates no source row, because nothing
 * uploaded it. The two arrive at the same content store by different routes and are
 * read by different calls.
 */
export const getResearch = (objectiveId: string) =>
  get<{ documents: ResearchDocument[] }>(
    `/api/research?objectiveId=${encodeURIComponent(objectiveId)}`,
  );

// ---- Sources ----

export interface Source {
  sourceId: string;
  objectiveId: string;
  kind: string;
  title?: string;
  status: 'pending' | 'extracted' | 'failed';
  detail?: string;
}

export interface ExtractedContent {
  title: string;
  language: string;
  method: string;
  producedAt: string;
  body: string;
  truncated: boolean;
}

async function get<T>(path: string): Promise<T | ApiError> {
  try {
    const response = await fetch(path, { credentials: 'same-origin' });
    const parsed = (await response.json()) as unknown;
    if (!response.ok) {
      return { error: (parsed as { error?: string }).error ?? 'request_failed', status: response.status };
    }
    return parsed as T;
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

export const listSources = (objectiveId: string) =>
  get<{ sources: Source[] }>(`/api/sources?objectiveId=${encodeURIComponent(objectiveId)}`);

export const readExtracted = (objectiveId: string, sourceId: string) =>
  get<{ content: ExtractedContent }>(
    `/api/sources/content?objectiveId=${encodeURIComponent(objectiveId)}&sourceId=${encodeURIComponent(sourceId)}`,
  );

export const presignUpload = (objectiveId: string, contentType: string, title: string) =>
  post<{ sourceId: string; url: string; key: string }>('/api/uploads', {
    objectiveId,
    contentType,
    title,
  });

// ---- The curriculum ----

/**
 * The whole node tree for one objective, already ordered by the API.
 *
 * Ordered *there*, not here: `gsi3` carries the order zero-padded so that a
 * lexicographic sort matches the learner's chosen order, and re-sorting the
 * result in the browser would be a second opinion about ordering that could
 * quietly disagree with the one the rest of the system uses.
 *
 * An empty `nodes` is the ordinary answer before anything has been built, not an
 * error — nothing is wrong with an objective that has no curriculum yet.
 */
export const getCurriculum = (objectiveId: string) =>
  get<{ nodes: CurriculumNode[]; curriculumVersion?: string }>(
    `/api/curriculum?objectiveId=${encodeURIComponent(objectiveId)}`,
  );

/**
 * Turning one subject on or off.
 *
 * One node per call rather than the whole selection, matching the endpoint: a
 * checkbox is one decision, and posting the entire set on every tap would make two
 * quick taps race to overwrite each other's *other* subjects, not just their own.
 *
 * The answer repeats `active` rather than being a bare `{ ok: true }`, and the
 * browser believes that over what it asked for — it is the only thing that knows
 * what is actually stored. `not_top_level` is a refusal by design: selection exists
 * only on top-level subjects, and a nested topic follows its parent.
 */
export const setNodeActive = (objectiveId: string, nodeId: string, active: boolean) =>
  post<{ ok: true; active: boolean }>('/api/curriculum/active', { objectiveId, nodeId, active });

// ---- Being reached ----

export interface WindowState {
  window: ReachWindow;
  /** Whether sending is stopped. See `setPaused`. */
  paused: boolean;
  /**
   * Why it stopped, when it has.
   *
   * `asked` is a setting the learner chose; `no_answer` is the escalation giving up
   * after three ignored reminders. The screen must say different things for each — one
   * is a decision, the other is something that happened to them — and a single
   * "paused" message for both would tell somebody who never touched it that they had.
   */
  pausedReason?: 'asked' | 'no_answer';
  /**
   * The learner's IANA zone, absent when we do not have one for them.
   *
   * Deliberately not filled in with a plausible default by the API: reachable hours
   * read in the wrong zone are worse than none, because `08:00`–`21:00` taken as UTC
   * is the middle of a Costa Rican night. The screen says so instead of showing
   * hours that will not be honoured.
   */
  timezone?: string;
  /** False until a window has actually been saved, which is when the defaults are labelled as such. */
  configured: boolean;
}

export const getWindow = () => get<WindowState>('/api/window');

/**
 * Stops or restarts the messages, without closing anything.
 *
 * Distinct from finishing an objective, which is the whole point: a busy week should
 * not cost a learner their progress. Everything is kept — the objective stays active
 * and every review state survives — and only the sending stops.
 */
export const setPaused = (paused: boolean) =>
  post<{ ok: true; paused: boolean }>('/api/pause', { paused });

/**
 * Saves the whole window at once.
 *
 * All three settings together rather than one at a time, matching the endpoint: they
 * only mean something as a set, and two quick taps sending partial writes would
 * leave a schedule neither of them chose. A refusal comes back as `invalid_window`
 * with `reason` naming which part was wrong.
 */
export const saveWindow = (window: ReachWindow) =>
  post<{ ok: true; window: ReachWindow; timezone?: string }>('/api/window', window);
